import { MigrationManifest } from 'redux-persist/es/types'

import { DeviceType } from '@/graphql/generated/schemas'

import { initialState as initialIncidentsState } from './incidents/incidentsSlice'
import { AppState } from './store'
import { initialState as initialUiState } from './ui/uiSlice'

export const migrations: MigrationManifest = {
  0: (state: AppState) => state,
  2: (state: AppState) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        sops: {
          ...state.ui.sops,
          filters: {
            nameLike: null,
            facilityId: null,
            createdAtRange: null,
            creatorId: null,
          },
          pageSize: 25,
        },
      },
    }
  },
  3: (state: AppState) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        drawers: {
          incident: {
            id: null,
            isOpen: false,
          },
          device: {
            id: null,
            deviceType: DeviceType.Camera,
            isOpen: false,
          },
          sop: {
            id: null,
            isOpen: false,
          },
        },
      },
    }
  },
  4: (state: AppState) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        notifications: {
          showUnread: false,
        },
        drawers: {
          ...state.ui?.drawers,
          sopIncident: {
            id: null,
            isOpen: false,
            sopNameLike: null,
          },
        },
      },
    }
  },
  5: (state: AppState) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        emergencyCall: {
          ...state.ui.emergencyCall,
          isOpen: false,
          isExpanded: true,
          isMuted: false,
          incident: {
            id: null,
            displayId: null,
            facilityName: null,
          },
        },
      },
    }
  },
  6: (state: AppState) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        outstandingIncidents: {
          ...state.ui.outstandingIncidents,
          filters: {
            facilities: [],
            deviceType: [],
            incidentTypes: [],
            guards: [],
            operators: [],
            timeOrder: [],
          },
          pageSize: 25,
        },
      },
    }
  },
  7: (state: AppState) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        drawers: {
          ...state.ui?.drawers,
          submittedTour: {
            id: null,
            isOpen: false,
          },
        },
      },
    }
  },
  8: (state: AppState): AppState => {
    return {
      ...state,
      ui: {
        ...state.ui,
        gatewayDevices: {
          pageSize: 25,
          filters: {
            nameLike: null,
          },
        },
      },
    }
  },
  9: (state: AppState): AppState => {
    return {
      ...state,
      incidents: initialIncidentsState,
    }
  },
  10: (state: AppState): AppState => {
    return {
      ...state,
      incidents: initialIncidentsState,
    }
  },
  // Update to ensure new initialIncidentsState mutedStatus is used
  11: (state: AppState): AppState => {
    return {
      ...state,
      incidents: initialIncidentsState,
    }
  },
  // Update to ensure new incident 'query' filter is used
  12: (state: AppState): AppState => {
    return {
      ...state,
      incidents: initialIncidentsState,
    }
  },
  // Add new tracelog drawer state
  13: (state: AppState): AppState => {
    return {
      ...state,
      ui: {
        ...state.ui,
        drawers: {
          ...state.ui?.drawers,
          incidentTraceLog: {
            ...state.ui.drawers?.incidentTraceLog,
            id: null,
            isOpen: false,
          },
        },
      },
    }
  },
  // Add new devices table pagesize
  14: (state: AppState): AppState => {
    return {
      ...state,
      ui: {
        ...state.ui,
        devices: {
          pageSize: 25,
          ...state.ui.devices,
        },
      },
    }
  },

  // Add new Guard tours filters
  15: (state: AppState): AppState => {
    return {
      ...state,
      ui: {
        ...state.ui,
        guardTours: {
          ...state.ui.guardTours,
          filters: {
            facilityIds: [],
            guardIds: [],
            status: [],
            submittedAtRange: null,
          },
        },
      },
    }
  },

  // Rename submitted tour to guard tour
  16: (state: AppState) => {
    const { submittedTours, drawers, ...restUi } = state.ui as any
    const { submittedTour, ...restDrawers } = drawers || {}

    return {
      ...state,
      ui: {
        ...restUi,
        guardTours: submittedTours || {
          pageSize: 25,
          filters: {
            facilityIds: [],
            guardIds: [],
            status: [],
            submittedAtRange: null,
          },
        },
        drawers: {
          ...restDrawers,
          guardTour: submittedTour || {
            id: null,
            isOpen: false,
          },
        },
      },
    }
  },
  // Add incident drawer navigation context
  17: (state: AppState): AppState => {
    return {
      ...state,
      ui: {
        ...state.ui,
        drawers: {
          ...state.ui?.drawers,
          incident: {
            ...state.ui.drawers?.incident,
            navigationContext: state.ui.drawers?.incident
              ?.navigationContext ?? {
              isFromIncidentsTable: false,
              incidents: [],
            },
          },
        },
      },
    }
  },
  // Add remoteActiveFilter, remoteActiveFilterCriteria, remoteActiveFilterLoaded to incidents slice
  18: (state: AppState): AppState => {
    return {
      ...state,
      incidents: initialIncidentsState,
    }
  },
  // Add highlightedEvent to incidentTraceLog drawer state
  19: (state: AppState): AppState => {
    return {
      ...state,
      ui: {
        ...state.ui,
        drawers: {
          ...state.ui?.drawers,
          incidentTraceLog: {
            ...state.ui.drawers?.incidentTraceLog,
            highlightedEvent: null,
          },
        },
      },
    }
  },
  // Add userSettingsLoaded to ui state
  20: (state: AppState): AppState => {
    return {
      ...state,
      ui: {
        ...state.ui,
        userSettingsLoaded: state.ui?.userSettingsLoaded ?? false,
      },
    }
  },
  // Remove persisted token slice (sensitive data should not live in localStorage)
  21: (state: AppState): AppState => {
    const { token: _legacyToken, ...rest } = state as AppState & {
      token?: unknown
    }
    return rest as AppState
  },
  // Add default associationStatus filter for devices
  22: (state) => {
    const s = state as any
    return {
      ...s,
      devices: {
        ...s.devices,
        filters: {
          ...s.devices?.filters,
          associationStatus: initialUiState.devices.filters.associationStatus,
        },
      },
    }
  },
  // Add incidentWorkflow drawer state (did not exist in previous persisted state).
  // Note: migrations for uiPersistConfig receive the UI slice directly (not the root state),
  // so drawers is at state.drawers, not state.ui.drawers.
  23: (state) => {
    const s = state as any
    return {
      ...s,
      drawers: {
        ...s.drawers,
        incidentWorkflow:
          s.drawers?.incidentWorkflow ??
          initialUiState.drawers.incidentWorkflow,
      },
    }
  },
  // Add isDarkMode preference to ui state (top-level, not under notifications).
  // Note: migrations for uiPersistConfig receive the UI slice directly.
  // Also check notifications.isDarkMode so migration 25 doesn't receive a
  // materialised false that shadows a persisted notifications.isDarkMode value.
  24: (state) => {
    const s = state as any
    return {
      ...s,
      isDarkMode: s.isDarkMode ?? s.notifications?.isDarkMode ?? false,
    }
  },
  // Move isDarkMode from notifications to top-level ui state.
  25: (state) => {
    const s = state as any
    const { isDarkMode: _notificationsIsDarkMode, ...restNotifications } =
      s.notifications ?? {}
    return {
      ...s,
      isDarkMode: s.isDarkMode ?? _notificationsIsDarkMode ?? false,
      notifications: restNotifications,
    }
  },
  // Add completedFtuIds (first-time-use completion tracking) to the ui slice.
  // Note: migrations for uiPersistConfig receive the UI slice directly, so the
  // field lives at state.completedFtuIds, not state.ui.completedFtuIds.
  26: (state) => {
    const s = state as any
    return {
      ...s,
      completedFtuIds: s.completedFtuIds ?? initialUiState.completedFtuIds,
    }
  },
  // Add deviceId + shouldForceDeviceTab to the incidentTraceLog drawer state
  // (Reasoning Trail "View access logs" deep-link). uiPersistConfig migrations
  // receive the UI slice directly, so drawers lives at state.drawers.
  27: (state) => {
    const s = state as any
    return {
      ...s,
      drawers: {
        ...s.drawers,
        incidentTraceLog: {
          ...s.drawers?.incidentTraceLog,
          deviceId: s.drawers?.incidentTraceLog?.deviceId ?? null,
          shouldForceDeviceTab:
            s.drawers?.incidentTraceLog?.shouldForceDeviceTab ?? false,
        },
      },
    }
  },
  // Dark mode becomes the default (ENG-33713). The toggle was gated behind the
  // development feature flag until now, so nobody outside HiveWatch has a
  // deliberate light preference to preserve — flip every persisted state to
  // dark once. uiPersistConfig migrations receive the UI slice directly, so
  // isDarkMode lives at state.isDarkMode, not state.ui.isDarkMode.
  28: (state) => {
    const s = state as any
    return {
      ...s,
      isDarkMode: true,
    }
  },
}
